import { DEFAULT_CATEGORY_IMAGES } from '@iheartjane/menu';
import {
  AlertIcon,
  Banner,
  Button,
  Flex,
  InfoIcon,
  Modal,
  Typography,
} from '@iheartjane/reefer';
import type { CategoryImageKey } from '@iheartjane/types';
import { titleCase } from '@iheartjane/utils';
import { useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { ImageUploader } from '../../ImageUploader';
import { isCustomCategoryImage } from '../CategoryImage.util';

export const CategoryImageModal = ({
  categoryKey,
  onRequestClose,
  open,
}: {
  categoryKey: CategoryImageKey | null;
  onRequestClose: () => void;
  open: boolean;
}) => {
  const [imageErrors, setImageErrors] = useState<string[]>([]);
  const { setValue, watch } = useFormContext();
  const categoryImage = watch(`category_images.${categoryKey}`);
  const isCustom = isCustomCategoryImage(categoryImage);

  // Only set the original image when the modal is open
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const originalImage = useMemo(() => (open ? categoryImage : null), [open]);

  if (!categoryKey) return null;

  const handleUpdateImage = (imageUrl: string) => {
    setValue(`category_images.${categoryKey}`, imageUrl, { shouldDirty: true });
  };

  const handleRevertToDefault = () => {
    handleUpdateImage(DEFAULT_CATEGORY_IMAGES[categoryKey] ?? '');
    setImageErrors([]);
  };

  const handleClose = () => {
    setImageErrors([]);
    onRequestClose();
  };

  return (
    <Modal
      onRequestClose={handleClose}
      open={open}
      overlayClose
      variant="standard-dialogue"
    >
      <Modal.Header
        actions={
          <>
            <Button
              label="Revert to default"
              disabled={!isCustom}
              onClick={handleRevertToDefault}
              variant="tertiary"
            />
            <Button
              ml={12}
              label="Save"
              disabled={categoryImage === originalImage}
              onClick={handleClose}
            />
          </>
        }
        title={titleCase(categoryKey)}
        subtitle={isCustom ? 'Custom' : 'Default'}
      />

      <Modal.Content>
        <Flex flexDirection="column" gap={24} width="100%">
          {imageErrors?.map((error, i) => (
            <Banner
              full
              icon={<AlertIcon />}
              label={error}
              variant="error"
              key={i}
            />
          ))}

          <Typography>
            Jane provides default images, but you can upload your own anytime.
          </Typography>

          <Flex flexDirection="column" gap={8}>
            <Flex
              justifyContent="center"
              background="grays-ultralight"
              borderRadius="lg"
              px={40}
              py={24}
            >
              <ImageUploader
                height={220}
                width={220}
                imageUrl={categoryImage}
                isLoading={false}
                onError={setImageErrors}
                onUpdateImage={handleUpdateImage}
              />
            </Flex>

            <Flex alignItems="center" gap={8}>
              <InfoIcon color="grays-mid" size="sm" />
              <Typography variant="small-body-regular" color="grays-mid">
                PNG or JPG up to 2MB, 200x200 recommended. Transparent PNGs work
                best with this style.
              </Typography>
            </Flex>
          </Flex>
        </Flex>
      </Modal.Content>
    </Modal>
  );
};
